import { MarketingNav, Footer } from "@/components/marketing/MarketingNav";

const UPDATED = "June 2026";

function LegalLayout({ title, children }) {
  return (
    <div className="bg-[var(--bg)] text-[var(--text)] min-h-screen">
      <MarketingNav />
      <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-[var(--text-muted)]">Last updated: {UPDATED}</p>
        <div className="mt-8 space-y-6 text-sm leading-relaxed text-[var(--text-sec)] legal-body">{children}</div>
      </article>
      <Footer />
    </div>
  );
}

function H({ children }) { return <h2 className="text-lg font-semibold text-[var(--text)] mt-8 mb-2">{children}</h2>; }

export function Terms() {
  return (
    <LegalLayout title="Terms of Service">
      <p>These Terms of Service ("Terms") govern your access to and use of Aurum AI ("Aurum", "we", "us"), a software platform that provides analytical tools for the XAU/USD (gold) market. By creating an account or using the service you agree to these Terms.</p>

      <H>1. What Aurum is — and is not</H>
      <p>Aurum is an educational and analytical tool. It calculates market context, risk parameters, and rule-based setups against a trading plan you define. <strong>Aurum does not provide financial, investment, tax, or legal advice, does not manage money, and does not execute trades on your behalf.</strong> All decisions to trade are yours alone.</p>

      <H>2. No guarantee of results</H>
      <p>Trading foreign exchange and precious metals involves substantial risk of loss and is not suitable for every investor. Past performance and backtested or simulated results do not guarantee future results. Nothing in the service is a promise or guarantee of profit. You may lose some or all of your capital.</p>

      <H>3. Eligibility & accounts</H>
      <p>You must be at least 18 years old and legally able to enter into these Terms. You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.</p>

      <H>4. Subscriptions & billing</H>
      <p>Paid plans (Pro, Elite) are billed in advance on a recurring monthly or annual basis through our payment processor, Stripe. By subscribing you authorize recurring charges until you cancel. Free trials, where offered, convert automatically to a paid subscription at the end of the trial unless cancelled beforehand. You can cancel at any time from your account; access continues until the end of the current billing period. Prices are shown in USD and exclude any applicable taxes.</p>

      <H>5. Acceptable use</H>
      <p>You agree not to misuse the service, including attempting to disrupt it, reverse-engineer it, resell access, or use it for unlawful purposes. We may suspend or terminate accounts that violate these Terms.</p>

      <H>6. Market data</H>
      <p>Market data is provided by third-party sources and may be delayed, incomplete, or contain errors. Aurum clearly labels data status (Live / Stale / Unavailable) but does not warrant the accuracy or timeliness of any data.</p>

      <H>7. Limitation of liability</H>
      <p>To the maximum extent permitted by law, Aurum and its affiliates shall not be liable for any trading losses or for any indirect, incidental, or consequential damages arising from your use of the service. The service is provided "as is" without warranties of any kind.</p>

      <H>8. Changes</H>
      <p>We may update these Terms from time to time. Material changes will be communicated in-app or by email. Continued use after changes take effect constitutes acceptance.</p>

      <H>9. Contact</H>
      <p>Questions about these Terms: <a className="text-aurum" href="mailto:support@goldaurumai.com">support@goldaurumai.com</a>.</p>
    </LegalLayout>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>This Privacy Policy explains what information Aurum AI collects, how we use it, and the choices you have. We collect only what we need to run the service.</p>

      <H>1. Information we collect</H>
      <p><strong>Account information:</strong> your name, email, and password (stored only as a secure hash). <strong>Trading plan & activity:</strong> the trading plan, paper trades, journal entries, and preferences you create in the app. <strong>Payment information:</strong> handled entirely by Stripe — we never see or store your full card number. <strong>Technical data:</strong> basic logs needed for security and reliability.</p>

      <H>2. How we use it</H>
      <p>To provide and personalize the service, calculate your analysis and risk, process subscriptions, send alerts and account emails you opt into, and keep the platform secure. We do not sell your personal information.</p>

      <H>3. Third-party processors</H>
      <p>We rely on trusted providers to operate: <strong>Stripe</strong> (payments), <strong>Twelve Data</strong> (market data), <strong>Resend</strong> (transactional & alert emails), and cloud hosting/database infrastructure. These providers process data only as needed to deliver their function.</p>

      <H>4. Email communications</H>
      <p>We send account and transactional emails necessary to run your subscription. Alert and digest emails are opt-in and can be turned off any time in Settings.</p>

      <H>5. Data retention & security</H>
      <p>We retain your data while your account is active. Passwords are hashed; data is transmitted over encrypted connections. You may request deletion of your account and associated data at any time.</p>

      <H>6. Your rights</H>
      <p>You may access, correct, export, or delete your personal data by contacting us. Depending on your location you may have additional rights under laws such as the GDPR or CCPA.</p>

      <H>7. Contact</H>
      <p>Privacy questions or requests: <a className="text-aurum" href="mailto:privacy@goldaurumai.com">privacy@goldaurumai.com</a>.</p>
    </LegalLayout>
  );
}

export function Refund() {
  return (
    <LegalLayout title="Refund & Cancellation Policy">
      <p>We want you to feel confident subscribing to Aurum AI. This policy explains cancellations, trials, and refunds.</p>

      <H>1. Free trial</H>
      <p>Eligible new Pro subscribers receive a 7-day free trial. You will not be charged during the trial. If you cancel before the trial ends, you are not charged. If you do not cancel, your subscription begins automatically at the end of the trial at the plan price shown at signup.</p>

      <H>2. Cancellation</H>
      <p>You can cancel your subscription at any time from your account settings. Cancellation stops future renewals; your plan remains active until the end of the current billing period, after which your account reverts to the Free plan. We do not lock you into long-term contracts.</p>

      <H>3. Refunds</H>
      <p>Because Aurum is a digital service delivered immediately, subscription fees are generally non-refundable for periods already started. However, we offer a <strong>14-day money-back guarantee on your first paid subscription</strong>: if you are unsatisfied, email us within 14 days of your first charge and we will refund that payment in full.</p>

      <H>4. Duplicate or erroneous charges</H>
      <p>If you believe you were charged in error or charged twice, contact us and we will investigate and refund any verified erroneous charge promptly.</p>

      <H>5. How to request</H>
      <p>Send refund or cancellation requests to <a className="text-aurum" href="mailto:billing@goldaurumai.com">billing@goldaurumai.com</a> from the email associated with your account. Refunds are issued to the original payment method via Stripe and may take several business days to appear.</p>
    </LegalLayout>
  );
}
